import React from "react";
import { Link } from "react-router-dom";
import { PageHeader, Card } from "@/components/Bits";
import { ArrowLeft } from "lucide-react";

export default function Terms() {
  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/auth" className="inline-flex items-center gap-2 text-xs text-[color:var(--fg-3)] hover:text-[color:var(--fg)] mb-6 font-mono-num tracking-wider" data-testid="back-to-auth">
          <ArrowLeft className="h-3 w-3" strokeWidth={1.5} /> BACK
        </Link>
        <PageHeader eyebrow="LEGAL" title="Terms of Service" />
        <div className="text-[11px] text-[color:var(--fg-3)] font-mono-num mb-8">EFFECTIVE: 2026-02-01 · LAST UPDATED: 2026-02-01</div>

        <Card eyebrow="1" title="Acceptance">
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed">
            By creating an account or using TradeGreen (&quot;the Service&quot;), you agree to these Terms of Service. If you do not agree, do not use the Service.
          </p>
        </Card>

        <Card eyebrow="2" title="What TradeGreen Is &mdash; And What It Isn't">
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed">
            TradeGreen is a <strong>journaling, analytics, and educational tool</strong> for traders. It records what you traded, computes performance statistics from your imported data, and provides AI-generated commentary intended for self-reflection.
          </p>
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed mt-3">
            TradeGreen is <strong>NOT</strong>: a registered investment advisor, a broker-dealer, a financial planner, or a fiduciary. We do not provide investment advice. Nothing on this site, including AI Coach output, is a recommendation to buy, sell, hold, or otherwise transact in any security, derivative, cryptocurrency, or other instrument.
          </p>
        </Card>

        <Card eyebrow="3" title="No Investment Advice · Risk Disclosure">
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed">
            <strong>Trading is risky.</strong> You can and will lose money &mdash; potentially more than your initial capital depending on the products you trade. Past performance shown by this tool (yours or anyone else&apos;s) does not guarantee future results.
          </p>
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed mt-3">
            You alone are responsible for every trading decision you make. You agree to consult a licensed financial professional before acting on any insight derived from TradeGreen.
          </p>
        </Card>

        <Card eyebrow="4" title="Your Account">
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed">
            You must be at least 18 years old (or the age of majority in your jurisdiction) and legally able to enter into contracts. You agree to keep your login credentials secure and to be fully responsible for all activity on your account.
          </p>
        </Card>

        <Card eyebrow="5" title="Subscriptions, Free Trial &amp; Refunds">
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed">
            New users get a 7-day free trial. After the trial, continued access requires a paid subscription, billed monthly via Stripe. You may cancel at any time from the Subscription page; access continues through the end of the current billing period.
          </p>
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed mt-3">
            Subscription fees are <strong>non-refundable</strong> except where required by law. Promotional codes (e.g. FREE4LYFE, SAVE50) are subject to their stated terms and may be revoked at any time.
          </p>
        </Card>

        <Card eyebrow="6" title="Your Data">
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed">
            You retain ownership of all trading data you upload. You grant TradeGreen a non-exclusive license to store, process, aggregate, and display your data solely to provide the Service to you. We will never sell your individual trading data.
          </p>
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed mt-3">
            Anonymous, aggregated statistics may be used to improve the Service. You can request data export or account deletion at any time by emailing support.
          </p>
        </Card>

        <Card eyebrow="7" title="AI Coach Output">
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed">
            The AI Coach (powered by Claude) produces text based on your trading data and general knowledge. AI output may be <strong>incorrect, incomplete, or misleading</strong>. Treat it as a starting point for self-reflection &mdash; never as professional advice.
          </p>
        </Card>

        <Card eyebrow="8" title="Acceptable Use">
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed">
            You agree not to: reverse-engineer the Service, share your account with others, attempt to access other users&apos; data, upload illegal or harmful content, or use the Service to deceive any regulator, broker, accountant, or other third party.
          </p>
        </Card>

        <Card eyebrow="9" title="Disclaimer of Warranties · Limitation of Liability">
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed uppercase tracking-wide font-mono-num text-[11px]">
            THE SERVICE IS PROVIDED &quot;AS IS&quot; WITHOUT WARRANTIES OF ANY KIND. TO THE FULLEST EXTENT PERMITTED BY LAW, TRADEGREEN AND ITS OPERATORS DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed mt-3 uppercase tracking-wide font-mono-num text-[11px]">
            IN NO EVENT WILL TRADEGREEN BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING TRADING LOSSES OR LOST PROFITS, ARISING OUT OF YOUR USE OF THE SERVICE. AGGREGATE LIABILITY IS LIMITED TO THE AMOUNT YOU PAID TRADEGREEN IN THE 12 MONTHS BEFORE THE CLAIM.
          </p>
        </Card>

        <Card eyebrow="10" title="Indemnification">
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed">
            You agree to indemnify and hold TradeGreen harmless from any claim, loss, or expense arising out of your use of the Service, your trading activity, or your violation of these Terms.
          </p>
        </Card>

        <Card eyebrow="11" title="Changes &amp; Termination">
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed">
            We may update these Terms at any time by posting the revised version. Continued use after changes are posted constitutes acceptance. We may suspend or terminate accounts that violate these Terms.
          </p>
        </Card>

        <Card eyebrow="12" title="Governing Law">
          <p className="text-sm text-[color:var(--fg-2)] leading-relaxed">
            These Terms are governed by the laws of the United States and the state in which TradeGreen&apos;s operator resides, without regard to conflict-of-law principles. Disputes will be resolved exclusively in courts of that jurisdiction.
          </p>
        </Card>

        <div className="text-[11px] text-[color:var(--fg-3)] font-mono-num text-center pt-8">
          BY USING TRADEGREEN YOU CONFIRM YOU HAVE READ, UNDERSTOOD, AND ACCEPTED THESE TERMS.
        </div>
      </div>
    </div>
  );
}
